export type ResearchJobKey = "daily" | "old-editions";
export type ResearchJobRunState = "running" | "succeeded" | "failed" | "stopped";
export type ResearchJobRun = {
  name: string;
  state: ResearchJobRunState;
  startedAt: number | null;
  finishedAt: number | null;
  dryRun?: boolean;
};
export type ResearchJobSummary = {
  job: ResearchJobKey;
  available: boolean;
  running: ResearchJobRun | null;
  lastFinished: ResearchJobRun | null;
  publications: string[] | null;
  unavailableReason?: string | null;
};
export type PublicationOption = {
  slug: string;
  label: string;
  tickable: boolean;
  note: string | null;
};

export const RESEARCH_PUBLICATIONS: readonly { slug: string; label: string }[] = [
  { slug: "emea", label: "Legal 500 EMEA" },
  { slug: "uk-sol", label: "Legal 500 UK Solicitors" },
  { slug: "apac", label: "Legal 500 Asia Pacific" },
  { slug: "latam", label: "Legal 500 Latin America" },
  { slug: "uk-bar", label: "Legal 500 UK Bar" },
  { slug: "us", label: "Legal 500 United States" },
  { slug: "de", label: "Legal 500 Deutschland" },
  { slug: "paris", label: "Legal 500 Paris" },
  { slug: "canada", label: "Legal 500 Canada" },
  { slug: "caribbean", label: "Legal 500 Caribbean" },
  { slug: "disputes-services", label: "Disputes Services" },
  { slug: "green-ambassador", label: "Green Ambassador" },
];

export const RESEARCH_JOB_SWITCHES: Record<ResearchJobKey, string> = {
  daily: "research-daily-job",
  "old-editions": "research-old-editions-job",
};

const labelFor = (slug: string): string =>
  RESEARCH_PUBLICATIONS.find(publication => publication.slug === slug)?.label ?? slug;

export function researchJobButtonState(
  summary: ResearchJobSummary,
  switchOn: boolean,
): { disabled: boolean; reason: string | null } {
  if (!summary.available) {
    return {
      disabled: true,
      reason: summary.unavailableReason || "This environment can't start this job.",
    };
  }
  if (summary.running) {
    return { disabled: true, reason: "A run of this job is already going." };
  }
  if (!switchOn) {
    return {
      disabled: true,
      reason: `The ${RESEARCH_JOB_SWITCHES[summary.job]} switch is off, so this job would do nothing.`,
    };
  }
  return { disabled: false, reason: null };
}

export function describeRunning(
  run: ResearchJobRun | null,
  format: (ms: number) => string,
): string | null {
  if (!run) return null;
  const prefix = run.dryRun ? "Dry run running" : "Running";
  return run.startedAt === null ? `${prefix}.` : `${prefix} since ${format(run.startedAt)}.`;
}

export function describeLastFinished(
  run: ResearchJobRun | null,
  format: (ms: number) => string,
): string | null {
  if (!run) return null;
  const result =
    run.state === "stopped" ? "was stopped" : run.state === "failed" ? "failed" : "succeeded";
  const when = run.finishedAt === null ? "" : `, finished ${format(run.finishedAt)}`;
  const kind = run.dryRun ? "Last run (dry run)" : "Last run";
  return `${kind} ${result}${when}.`;
}

const emeaTickable = (switches: Record<string, boolean>): boolean =>
  Object.entries(switches).some(([key, on]) => key.startsWith("sync-emea-") && on === true);

export function publicationOptions(
  summary: ResearchJobSummary,
  switches: Record<string, boolean>,
): PublicationOption[] {
  const option = (slug: string, label: string, tickable: boolean): PublicationOption => ({
    slug,
    label,
    tickable,
    note: tickable ? null : "switch off",
  });
  if (summary.job === "old-editions") {
    return (summary.publications ?? []).map(slug =>
      option(slug, labelFor(slug), slug === "emea" ? emeaTickable(switches) : true),
    );
  }
  return RESEARCH_PUBLICATIONS.map(({ slug, label }) =>
    option(
      slug,
      label,
      slug === "emea" ? emeaTickable(switches) : switches[`research-daily-${slug}`] === true,
    ),
  );
}

export function startBody(
  ticked: string[],
  options: PublicationOption[],
  dryRun = false,
): { publications?: string[]; dryRun?: boolean } {
  const tickable = options.filter(option => option.tickable);
  const inOrder = tickable
    .filter(option => ticked.includes(option.slug))
    .map(option => option.slug);
  const body: { publications?: string[]; dryRun?: boolean } =
    inOrder.length === tickable.length ? {} : { publications: inOrder };
  if (dryRun) body.dryRun = true;
  return body;
}

export function initialTicked(options: PublicationOption[]): string[] {
  return options.filter(option => option.tickable).map(option => option.slug);
}

export function canStart(ticked: string[]): boolean {
  return ticked.length > 0;
}

export function willRunText(ticked: string[], options: PublicationOption[]): string {
  const labels = options.filter(option => ticked.includes(option.slug)).map(option => option.label);
  const list =
    labels.length > 1
      ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
      : labels[0];
  return `This run covers ${list}.`;
}

export function selectAllState(
  ticked: string[],
  options: PublicationOption[],
): "all" | "some" | "none" {
  const tickable = options.filter(option => option.tickable);
  const count = tickable.filter(option => ticked.includes(option.slug)).length;
  if (count === 0) return "none";
  return count === tickable.length ? "all" : "some";
}

export function toggleAll(ticked: string[], options: PublicationOption[]): string[] {
  return selectAllState(ticked, options) === "all" ? [] : initialTicked(options);
}
